import { createSelector } from 'reselect';

import type BusinessRule from '~/types/BusinessRule';

import { ruleReprocessingStatus } from '~/constants/rules';
import { type ApplicationState } from '~/store';
import { type AutomationRuleError } from '~/types/AutomationRuleError';

const getAutomationsStore = (state: ApplicationState) => state.automations;

// returns array of package template object ids that have rules associated
export const getPackageTemplateIdsWithRules = createSelector(
  getAutomationsStore,
  (automationsStore): string[] => automationsStore.associatedPackageTemplateIds,
);

// returns rules except default, for use in custom rule section
export const getCustomRules = createSelector(
  getAutomationsStore,
  (automationsStore): BusinessRule[] =>
    automationsStore.businessRules.filter((rule) => rule.rankDesc !== 0),
);

export const getDeleteRuleModalIsOpen = createSelector(
  getAutomationsStore,
  (automationsStore) => automationsStore.deleteRuleModalIsOpen,
);

export const getAddRuleModalIsOpen = createSelector(
  getAutomationsStore,
  (automationsStore) => automationsStore.addRuleModalIsOpen,
);

export const getAddRuleSuccess = createSelector(
  getAutomationsStore,
  (automationsStore) => automationsStore.addRuleSuccess,
);

export const getAutomationRuleError = createSelector(
  getAutomationsStore,
  ({ error }): AutomationRuleError => error,
);

export const getReprocessRuleState = createSelector(
  getAutomationsStore,
  (automationsStore): string => automationsStore.reprocessRuleStatus,
);

export const isRuleReprocessingPending = createSelector(
  getAutomationsStore,
  (automationsStore): boolean =>
    automationsStore.reprocessRuleStatus === ruleReprocessingStatus.pending,
);

export const hasClosedReprocessingPendingNotification = createSelector(
  getAutomationsStore,
  (automationsStore): boolean => automationsStore.closedReprocessingPendingNotification,
);

export const hasClosedReprocessingCompleteNotification = createSelector(
  getAutomationsStore,
  (automationsStore): boolean => automationsStore.closedReprocessingCompleteNotification,
);

export const hasAutomations = createSelector(
  getAutomationsStore,
  (automationsStore): boolean => automationsStore.businessRules.length > 0,
);
